import style from "./email-popup.module.css";
import CloseIcon from "@mui/icons-material/Close";
import { CheckCircle, ErrorOutline, Send } from "@mui/icons-material";
import { Autocomplete, Chip, CircularProgress, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { emailAsset } from "../../service/productsService";
import useEscapeKey from "../../hooks/useEscapeKey";
import useUser from "../../hooks/useUser";
import { useTranslation } from "react-i18next";
import { usePushToDataLayer } from "../../hooks/usePushToDataLayer";
import { GTM_EVENTS } from "../../utils/constants";

export default function EmailPopup({
	pimcoreId,
	isProduct,
	active,
	setClose,
	customStyles,
	searchData,
	sortModel,
	isProductAsset = false,
	setSnackbarOpen,
	setSnackbarMessage,
	setSnackbarSeverity,
	setIsProcessing,
	getAllResult,
	selectedIds,
	subTitle,
	handleSend,
	isItemsFetching = false,
	isItemSuccess = false,
}) {
	//todo EMAIL
	const { t } = useTranslation();
	const { activeUserEmail } = useUser();
	const [emails, setEmails] = useState([]);
	const [sending, setSending] = useState(false);
	const [showSuccess, setShowSuccess] = useState(false);
	const queryclient = useQueryClient();
	const pushToDL = usePushToDataLayer();

	useEscapeKey(() => {
		setClose(false);
	});

	const { isError, isSuccess, isFetching } = useQuery({
		//TODO rate limiting
		queryKey: ["sendEmail"],
		queryFn: () => {
			return emailAsset(
				pimcoreId,
				isProduct,
				emails.map(({ value }) => value)
			);
		},
		enabled: sending && isProductAsset,
		retry: false,
		staleTime: 0,
	});

	const isGetAllResult = Boolean(getAllResult);
	const { isDownloadAllError, isDownloadAllSuccess, isDownloadAllFetching, data: downloadAllData } = useQuery({
		queryKey: ["sendEmail"],
		queryFn: () => {
			return getAllResult?.(
				sortModel,
				searchData,
				true,
				emails.map(({ value }) => value),
				selectedIds ?? []
			) ?? Promise.resolve(undefined);
		},
		enabled: sending && isGetAllResult,
		retry: false,
	});

	const isDataError = isError ||  isDownloadAllError;
	const isDataSuccess = isSuccess ||  isDownloadAllSuccess;
	const isDataFetching = isFetching ||  isDownloadAllFetching || isItemsFetching;

	useEffect(() => {
		if (emails.length === 0) {
			activeUserEmail ? setEmails([{ label: activeUserEmail, value: activeUserEmail }]) : setEmails([{ label: null, value: null }]);
		}
		// Reset success state when dialog opens
		if (active) {
			setShowSuccess(false);
			setSending(false);
		}
	}, [active]);

	useEffect(() => {
		let timer;
		if (isSuccess || isDownloadAllSuccess) {
			// Check if there's a warning message in the download response
			const warning = downloadAllData?.message || downloadAllData?.warning;
			
			setShowSuccess(!warning); // Only show success UI if no warning
			timer = setTimeout(() => {
				queryclient.resetQueries({ queryKey: ["sendEmail"], exact: true });
				setClose(false);
				setIsProcessing?.(false);
				setShowSuccess(false);
				
				if (warning) {
					// Show warning message instead of success
					setSnackbarMessage(warning);
					setSnackbarSeverity("warning");
				} else {
					// Only show success if email was actually sent (no warning)
					setSnackbarMessage(t("Email sent successfully."));
					setSnackbarSeverity("success");
				}
				setSnackbarOpen(true);
			}, warning ? 500 : 2000); // Shorter delay for warnings
		}
		if (isError || isDownloadAllError) {
			setIsProcessing?.(false)
			setSnackbarMessage(t("Something went wrong. Try again Later"));
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		}
		setSending(false);
		return () => {
			if (timer) clearTimeout(timer);
		};
	}, [isDataError, isDataSuccess]);

	const handleSendMail = () => {
		if (emails.length !== 0) {
			if (Boolean(handleSend)) {
				handleSend?.('email', emails?.map(({ value }) => value));
			} else {
				setSending(true);
				setIsProcessing?.(true);
				setSnackbarMessage(t("Processing..."));
				setSnackbarSeverity("info");
				setSnackbarOpen(true);
			}
			pimcoreId && pushToDL(GTM_EVENTS.PAGE_LOAD,{
				button_text: 'Send',
				asset_id: pimcoreId 
			})
		} else {
			setSnackbarMessage(t("Email not entered."));
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		}
	};

	return (
		<div
			className={active ? "backdrop backdrop--show" : "backdrop"}
			style={{ zIndex: "var(--zIndexEmailPopUp)", ...(customStyles || {}) }}
			onClick={() => {
				setClose(false);
			}}>
			<div
				className={style.emailPopUp}
				onClick={(event) => {
					event.stopPropagation();
				}}>
				<h3>{t("Email Details")}</h3>
				<p className={style.subHeading}>
					{subTitle}
				</p>
				<Autocomplete
					multiple
					freeSolo
					selectOnFocus
					clearOnBlur
					handleHomeEndKeys
					options={emails.map(({ label }) => label)}
					value={emails}
					onChange={(event, newValue) => {
						const newState = newValue.map((value) => {
							if (typeof value === "string") {
								return {
									label: value,
									value,
								};
							} else if (value && value.inputValue) {
								return {
									label: value.inputValue,
									value: value.inputValue,
								};
							} else {
								return value;
							}
						});

						setEmails(newState);
					}}
					filterOptions={(options, params) => {
						const { inputValue } = params;
						const filtered = [];
						const isExisting = options.some((label) => label.startsWith(inputValue));
						if (inputValue !== "" && !isExisting) {
							filtered.push({ label: `Add "${inputValue}"`, inputValue });
						}
						return filtered;
					}}
					renderTags={(value, getTagProps) =>
						value.map((option, index) => {
							const { key, ...tagProps } = getTagProps({ index });
							return <Chip variant='filled' label={option?.label} key={key} {...tagProps} />;
						})
					}
					sx={{ flexGrow: "1" }}
					renderInput={(params) => <TextField {...params} variant='standard' label='To :' placeholder={t("Email")} />}
				/>

				<div className={style.sendButton}>
					<button
						className={showSuccess || isItemSuccess ? "btn btn-success" : "btn btn-primary"}
						onClick={() => handleSendMail()}
						disabled={ isDataFetching || showSuccess || isItemSuccess }>
						{isDataFetching ? (
							<div className='btn-icon'>
								<CircularProgress size='20px' sx={{ alignSelf: "center" }} color='secondary' />
								{t("Sending")}
							</div>
						) : showSuccess || isItemSuccess ? (
							<div className='btn-icon'>
								<CheckCircle />
								<b>{t("Sent")}</b>
							</div>
						) : (
							<div className='btn-icon'>
								<Send />
								{t("Send")}
							</div>
						)}
					</button>
				</div>
				{isDataError && (
					<div className={style.error}>
						<div className='btn-icon'>
							<ErrorOutline />
						</div>
						{t("Something went wrong. Try again Later")}
					</div>
				)}
				<div
					className='close-button'
					onClick={() => {
						setClose(null);
					}}>
					<CloseIcon fontSize='large' />
				</div>
			</div>
		</div>
	);
}
